import React, { useState } from 'react';
import { Modal, Button, Form, Input, Select, notification } from 'tea-component';
import UploadFile, { UploadFileResult } from '@/pages/datalab/components/UploadFile';
import { useForm, useField } from 'react-final-form-hooks';
import { useRequest } from 'ahooks';
import { commitNumberPackage } from '@dbl/services/numberPackage';
import Loading from '@/pages/datalab/components/Loading';
import moment from 'moment';
import './index.scss';

export default function UploadPackage(props: { gameCode: string; refreshUserPackagesTable: () => void; onClose: () => void; formatMessage: (id: string) => string; uploadFormatMessage: (id: string) => string;}) {
  const { gameCode, refreshUserPackagesTable, onClose, formatMessage, uploadFormatMessage } = props;

  const [uploadFileResult, setUploadFileResult] = useState<UploadFileResult>();
  const [showInputError, setShowInputError] = useState<boolean>(false);

  const { loading, run } = useRequest((params) => commitNumberPackage(params), {
    manual: true,
    onSuccess: () => {
      notification.success({
        description: formatMessage("save_success", {name: name.input.value.trim()}),
      });
      refreshUserPackagesTable();
      onClose();
    },
  });

  const onSubmit = () => {
    const params = {
      game_code: gameCode,
      name: name.input.value.trim(),
      column: column.input.value,
      desc: desc.input.value.trim(),
      md5: uploadFileResult?.md5,
      create_time: moment().format('YYYY-MM-DD HH:mm:ss'),
    };
    run(params);
  };

  const { form, handleSubmit, validating } = useForm({
    onSubmit,
    initialValuesEqual: () => true,
    validate: ({ name, column }: { name: string; column: string; }) => ({
      name: !name?.trim() ? formatMessage("not_empty") : undefined,
      column: !column ? formatMessage("extracted_fields_placeholder") : undefined,
    }),
  });

  const name = useField('name', form);
  const column = useField('column', form);
  const desc = useField('desc', form);
  return (
    <Loading loading={loading}>
      <Modal.Body>
        <UploadFile
          uploadFormatMessage={uploadFormatMessage}
          action={`${window.location.protocol}//${window.location.host}/api/v1/datalab/uploadFile`}
          uploadEnd={(result: UploadFileResult) => {
            setUploadFileResult(result);
          }}
        />

        <Form style={{ marginTop: 20 }} className="uploadPackage-modal">
          <Form.Item
            label={formatMessage("name")}
            showStatusIcon={false}
            status={getStatus(name.meta, validating, showInputError)}
            message={getStatus(name.meta, validating, showInputError) === 'error' && name.meta.error}
          >
            <Input
              maxLength={100}
              placeholder={formatMessage("name_placeholder")}
              style={{ width: '100%' }}
              autoComplete="off"
              {...name.input}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage("extracted_fields")}
            showStatusIcon={false}
            status={getStatus(column.meta, validating, showInputError)}
            message={getStatus(column.meta, validating, showInputError) === 'error' && column.meta.error}
          >
            <Select
              {...column.input}
              placeholder={formatMessage("extracted_fields_placeholder")}
              style={{ width: '100%' }}
              appearance="button"
              matchButtonWidth
              options={ColumnOptions}
            />
          </Form.Item>
          <Form.Item label={formatMessage("remarks")} showStatusIcon={false}>
            <Input
              maxLength={300}
              placeholder={formatMessage("remarks_placeholder")}
              style={{ width: '100%' }}
              autoComplete="off"
              {...desc.input}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            setShowInputError(true);
            handleSubmit();
          }}
          disabled={!uploadFileResult?.res}
        >
          {formatMessage("save")}
        </Button>
        <Button htmlType="button" onClick={onClose}>
          {formatMessage("cancel")}
        </Button>
      </Modal.Footer>
    </Loading>
  );
}

const ColumnOptions = [
  {
    value: 'vopenid',
    text: 'vopenid',
  },
  {
    value: 'deviceid',
    text: 'deviceid',
  },
  {
    value: 'ip',
    text: 'ip',
  },
  {
    value: 'uid',
    text: 'uid',
  },
  {
    value: 'uuid',
    text: 'uuid',
  },
];

function getStatus(meta: any, validating: any, showError: boolean): 'validating' | 'error' | 'success' | undefined {
  if (meta.active && validating) {
    return 'validating';
  }
  if (!meta.touched) {
    return undefined;
  }
  if (!showError) {
    return undefined;
  }
  return meta.error ? 'error' : 'success';
}
